import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiV1WsPath } from "@/lib/api";
import type { ApprovalTask } from "@/lib/types";

// Backend liefert {tasks: ApprovalTask[]} (envelope MyOpenApprovalsResponse).
// Wir unwrappen hier, damit alle Aufrufer einfach mit einem Array arbeiten.
type RawMyOpenResponse = { tasks?: ApprovalTask[] } | ApprovalTask[] | null | undefined;

function unwrapTasks(raw: RawMyOpenResponse): ApprovalTask[] {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.tasks)) return raw.tasks;
  return [];
}

export function useMyApprovals(
  workspaceId: string | null,
  filter: "mine" | "delegated" | "all" = "mine",
) {
  return useQuery({
    queryKey: ["approvals", workspaceId, filter],
    queryFn: async () => {
      const raw = await apiClient.get<RawMyOpenResponse>(
        apiV1WsPath(workspaceId!, "approvals/my-open"),
        { query: { filter } },
      );
      return unwrapTasks(raw);
    },
    enabled: !!workspaceId,
  });
}

export function useApproveTask(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, comment }: { taskId: string; comment?: string }) =>
      apiClient.post(
        apiV1WsPath(workspaceId!, `approvals/${taskId}/approve`),
        { comment },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", workspaceId] }),
  });
}

export function useRejectTask(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      reasonCode,
      comment,
    }: {
      taskId: string;
      reasonCode: string;
      comment: string;
    }) =>
      apiClient.post(
        apiV1WsPath(workspaceId!, `approvals/${taskId}/reject`),
        { reasonCode, comment },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", workspaceId] }),
  });
}

export function useDelegateTask(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      delegateTo,
      comment,
    }: {
      taskId: string;
      delegateTo: string;
      comment?: string;
    }) =>
      apiClient.post(
        apiV1WsPath(workspaceId!, `approvals/${taskId}/delegate`),
        { delegateTo, comment },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", workspaceId] }),
  });
}

export function useRequestInfo(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, question }: { taskId: string; question: string }) =>
      apiClient.post(
        apiV1WsPath(workspaceId!, `approvals/${taskId}/request-info`),
        { question },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", workspaceId] }),
  });
}

export function useAddComment(workspaceId: string | null) {
  return useMutation({
    mutationFn: async ({ invoiceId, text }: { invoiceId: string; text: string }) =>
      apiClient.post(
        apiV1WsPath(workspaceId!, `invoices/${invoiceId}/comments`),
        { text },
      ),
  });
}

export function useBulkApprove(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskIds, comment }: { taskIds: string[]; comment?: string }) =>
      apiClient.post(
        apiV1WsPath(workspaceId!, "bulk-approve"),
        { taskIds, comment },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["approvals", workspaceId] }),
  });
}
